import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Container, Label } from "reactstrap";
import { useWebChatCore } from "../../../cores/webChat";
import { Conversation } from "../../../cores/webChat/interfaces/api/get-conversations";
import { PanelWembii } from "../PanelWembii";
import { useSocketCore } from "../../../cores/socket";
import { getColor, getFirstLetters } from "../../utils/stringUtils";
import { InputWembii } from "../InputWembii";
import { useMeCore } from "../../../cores/me";
import { useSocialChatCore } from "../../../cores/socialChat";
import Avatar from "../Avatar";
import {
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  WHATSAPP_COLOR,
} from "../../../constants/socialColors.constant";

interface Props {
  onSelect: (arg0: string) => void;
}

export default function ConversationList({ onSelect }: Props) {
  const { user, company } = useMeCore();
  const { conversations: webConversations, selected } = useWebChatCore();
  const {
    facebookConversations,
    instagramConversations,
    whatsappConversations,
  } = useSocialChatCore();
  const { onlineClients, subscribeToChannel, leaveFromChannel } =
    useSocketCore();
  const [filter, setFilter] = useState("");
  const [searchResults, setSearchResults] = useState<Conversation[]>([]);

  const conversations = useMemo(
    () =>
      [
        ...webConversations,
        ...facebookConversations,
        ...instagramConversations,
        ...whatsappConversations,
      ].sort((a, b) => (b.timestamp.full < a.timestamp.full ? -1 : 1)),
    [
      webConversations,
      facebookConversations,
      instagramConversations,
      whatsappConversations,
    ]
  );

  useEffect(() => {
    const results = conversations.filter((conversation) =>
      conversation.participants.find((participant) =>
        participant.name.toLowerCase().includes(filter)
      )
    );
    setSearchResults(results);
  }, [filter, conversations]);

  const itsOnline = useCallback(
    (id: string) => onlineClients.includes(id),
    [onlineClients]
  );

  const handleSelectConversation = useCallback(
    async (conversation) => {
      if (selected && selected.type === "web") leaveFromChannel(selected.room!);
      onSelect(conversation);
    },
    [onSelect, selected, leaveFromChannel]
  );

  useEffect(() => {
    if (user && user.id && selected && selected.room) {
      subscribeToChannel(user?.id!, selected.id, selected.room, company?.id!);
    }
  }, [selected, user, subscribeToChannel, company]);

  const getParticipantNames = useCallback(
    (conversation: Conversation) =>
      conversation.participants
        .map((participant) => participant.name)
        .join(", "),
    []
  );

  return (
    <Container className="p-0">
      <InputWembii
        type="text"
        placeholder="Búsqueda"
        rounded
        showShadow
        className="border-0"
        onChange={(v) => setFilter(v)}
        icon={
          <FontAwesomeIcon
            icon={["fas", "search"]}
            size="1x"
            color="grey"
            className="pointer"
          />
        }
      />

      {searchResults.map((conversation) => (
        <PanelWembii
          key={conversation.id}
          shadow
          className="position-relative pointer p-3 mb-3"
        >
          <div
            className="d-flex"
            onClick={() => handleSelectConversation(conversation)}
          >
            {conversation.type === "facebook" && (
              <FontAwesomeIcon
                className=""
                icon={["fab", "facebook"]}
                size="lg"
                color={FACEBOOK_COLOR}
              />
            )}
            {conversation.type === "instagram" && (
              <FontAwesomeIcon
                className=""
                icon={["fab", "instagram"]}
                size="lg"
                color={INSTAGRAM_COLOR}
              />
            )}
            {conversation.type === "whatsapp" && (
              <FontAwesomeIcon
                className=""
                icon={["fab", "whatsapp"]}
                size="lg"
                color={WHATSAPP_COLOR}
              />
            )}
            {conversation.type === "web" && (
              <div
                className={cn("rounded-circle bg-secondary", {
                  "bg-success": itsOnline(conversation.participants[0].id),
                })}
                style={{
                  width: 16,
                  height: 16,
                }}
              ></div>
            )}
            <Avatar
              className="mr-2"
              label={getFirstLetters(conversation.participants[0].name)}
              image={conversation.participants[0].avatarUrl}
              backgroundColor={getColor(parseInt(conversation.id))}
              color="#fff"
              width={"56px"}
              height={"56px"}
            />
            <div className="w-75">
              <Label className="text-primary pointer font-weight-bold m-0 d-flex justify-content-between align-items-center text-truncate">
                {`${getParticipantNames(conversation)}`}
                {conversation.unreadCount > 0 && (
                  <Badge color="danger" className="text-white mt-2" pill>
                    {conversation.unreadCount}
                  </Badge>
                )}
              </Label>
              <Label className="text-muted text-truncate w-100 m-0">
                {conversation.snippet}
              </Label>
            </div>
          </div>
          <Label
            className="d-flex text-muted position-absolute m-0 pr-2 float-right float-top-right"
            size="sm"
          >
            {conversation.timestamp.date}
          </Label>
        </PanelWembii>
      ))}
    </Container>
  );
}
